import { ArrowUpRight, Award, Camera, Copy, Eye, MapPin, Phone, Plane, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Reveal from "./Reveal";
import { BRAND, FOOTER, VISIT } from "@/config/clinicConfig";

const CLINIC_IMAGE =
  "https://customer-assets-jai6qajn.emergentagent.net/job_instant-site-151/artifacts/wfhnd61o_20260303_120758.webp";
const BUILDING_IMAGE = "/visit/building.jpg";
const INTERIOR_IMAGE = "/visit/interior.jpg";

const TRUST = [
  { icon: ShieldCheck, text: "Corneal shields every session" },
  { icon: Award, text: "The finest protocols in the U.S." },
  { icon: Eye, text: "Free online consultations" },
];

export default function Footer() {
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(VISIT.address.formatted);
      toast.success("Address copied to clipboard");
    } catch {
      toast.error(VISIT.address.formatted);
    }
  };

  return (
    <footer id="visit" data-testid="footer-section" className="bg-espresso text-cream">
      <div className="mx-auto max-w-7xl px-5 pt-12 pb-24 lg:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-medium tracking-tight sm:text-4xl lg:text-[2.75rem]">
            {VISIT.heading}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-cream/70 sm:text-base">{VISIT.note}</p>
          <a
            data-testid="footer-booking-link"
            href={BRAND.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-sm font-semibold text-cream transition-[background-color,transform] duration-300 hover:bg-claydark hover:-translate-y-0.5"
          >
            {BRAND.bookingLabel}
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
          </a>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-7 gap-y-2" data-testid="footer-trust-points">
            {TRUST.map(({ icon: Icon, text }) => (
              <span key={text} className="flex items-center gap-2 text-[13px] font-medium text-cream/85">
                <Icon size={15} className="text-gold" />
                {text}
              </span>
            ))}
          </div>
          <p className="mt-4 text-[13px] text-cream/60">
            Already booked?{" "}
            <a
              data-testid="footer-consult-link"
              href={BRAND.consultUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-gold underline decoration-gold/40 underline-offset-4 transition-colors duration-300 hover:text-cream"
            >
              Open your consult portal
            </a>{" "}
            to submit photos and messages.
          </p>
        </Reveal>

        <div className="mt-20 grid items-stretch gap-8 lg:grid-cols-2">
          <Reveal className="flex flex-col justify-between rounded-3xl border border-cream/15 p-8">
            <div>
              <div className="mb-7" data-testid="visit-photos">
                <img
                  src={BUILDING_IMAGE}
                  alt="My Salon Suites building entrance on Northern Ave, Peoria"
                  className="aspect-[16/10] w-full rounded-2xl object-cover object-top"
                />
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <img
                    src={INTERIOR_IMAGE}
                    alt="Hallway entrance to Beauté Solutions, Suite 125"
                    loading="lazy"
                    className="aspect-[4/3] w-full rounded-2xl object-cover object-top"
                  />
                  <img
                    src={CLINIC_IMAGE}
                    alt="Inside the Beauté Solutions treatment room"
                    loading="lazy"
                    className="aspect-[4/3] w-full rounded-2xl object-cover"
                  />
                </div>
              </div>
              <p className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-gold">
                <MapPin size={14} />
                Clinic address
              </p>
              <p className="mt-5 font-display text-xl font-semibold text-gold">My Salon Suites</p>
              <p className="mt-1 font-display text-2xl font-medium leading-snug">
                {VISIT.address.street}
                <br />
                {VISIT.address.city}, {VISIT.address.state} {VISIT.address.zip}
              </p>
              <p className="mt-2 text-sm text-cream/70">(Inside Park West Plaza on Northern Ave)</p>
              <div className="mt-6" data-testid="footer-phone-block">
                <a
                  data-testid="footer-phone-link"
                  href={`tel:${VISIT.phone.replace(/[^0-9]/g, "")}`}
                  className="inline-flex items-center gap-2.5 font-display text-2xl font-medium transition-colors duration-300 hover:text-gold"
                >
                  <Phone size={20} className="text-gold" />
                  {VISIT.phone}
                </a>
                <p className="mt-1.5 text-sm text-cream/70">{VISIT.phoneNote}</p>
              </div>
            </div>
            <div className="mt-10 space-y-4">
              <button
                data-testid="copy-address-button"
                onClick={copyAddress}
                className="inline-flex items-center gap-2 rounded-full border border-cream/25 px-5 py-2.5 text-[13px] font-semibold text-cream transition-[background-color,border-color] duration-300 hover:border-gold hover:text-gold"
              >
                <Copy size={14} />
                Copy address
              </button>
              <div
                data-testid="sky-harbor-note"
                className="rounded-2xl border border-gold/30 bg-cream/[0.06] p-6"
              >
                <p className="flex items-start gap-3 text-[15px] font-semibold leading-relaxed text-cream">
                  <Plane size={19} className="mt-1 shrink-0 text-gold" />
                  <span>{VISIT.flyingIn}</span>
                </p>
                <p className="mt-4 flex items-start gap-3 text-[15px] font-semibold leading-relaxed text-cream/90">
                  <Camera size={19} className="mt-1 shrink-0 text-gold" />
                  <span>{VISIT.bookingNote}</span>
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="h-full min-h-[320px] overflow-hidden rounded-3xl border border-cream/15">
              <iframe
                data-testid="map-embed"
                title="Clinic location map"
                src={VISIT.mapEmbedUrl}
                className="h-full min-h-[320px] w-full"
                style={{ border: 0, filter: "sepia(0.25) saturate(0.9)" }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>

        <div className="mt-20 border-t border-cream/10 pt-8">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center">
              <img
                src="/logo-gold.png"
                alt="Beauté Solutions — Cosmetic Esthetics"
                className="h-12 w-auto"
              />
            </div>
            <p className="text-[13px] text-cream/50">
              © {new Date().getFullYear()} Beauté Solutions. All rights reserved.
            </p>
          </div>
          <p className="mt-6 max-w-3xl text-[12px] leading-relaxed text-cream/40">{FOOTER.disclaimer}</p>
        </div>
      </div>
    </footer>
  );
}
